import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../auth/services/auth.service';
import { LicenseStateService } from '../../services/license-state-service/license-state-service';
import { SchoolProfileIdentification } from '../../../schools/models/school-profile.model';
import { LicenseType } from '../../../school-license/models/school-license.model';
import { SchoolSizeBand } from '../../../school-license/models/license-pricing.model';
import { ErrorReportingService } from '../../services/error-reporting-service/error-reporting-service';

/** Kolik měsíců se v nabídce ukáže — od prvního objednatelného dál. */
const MONTH_OPTION_COUNT = 12;

/**
 * Poslední den v měsíci, kdy si lze licenci objednat ještě od tohoto měsíce. Od 16. dne je
 * nejbližší volbou měsíc následující.
 */
export const ORDER_DEADLINE_DAY = 15;

/** Co si volající přeje objednat, když chat otevře zvenčí. */
export type OrderKind = 'license' | 'credits';

export interface MonthOption {
  /** První den měsíce v ISO tvaru (`2026-09-01`) — to, co jde do textu zprávy. */
  value: string;
  /** Český popisek, např. „září 2026“. */
  label: string;
}

/**
 * Podklady pro objednávku licence z chatovacího okna: identifikační údaje školy, které se doplní
 * jako fakturační údaje, a nabídka měsíců, od kterých licence může začít.
 *
 * Údaje se stahují až ve chvíli, kdy uživatel objednávku otevře ({@link enable}) — chat visí na
 * každé stránce, ale objednávku otevře málokdo.
 */
@Injectable({ providedIn: 'root' })
export class LicenseOrderService {
  private readonly authService = inject(AuthService);
  private readonly licenseState = inject(LicenseStateService);
  private readonly errorReporting = inject(ErrorReportingService);

  readonly #enabled = signal(false);

  /** Okamžik posledního otevření objednávky — od něj se odvíjí nabídka měsíců. */
  readonly #openedAt = signal(Date.now());

  /**
   * Škola, za kterou se objednává, když ji volající zná lépe než {@link LicenseStateService} —
   * typicky stránka licencí, kterou si správce systému otevře pro cizí školu. `null` = řídí se
   * aktuálním kontextem aplikace.
   */
  readonly #schoolIdOverride = signal<string | null>(null);

  readonly #orderRequests = signal(0);

  readonly #requestedTier = signal<LicenseType | null>(null);
  readonly #requestedSize = signal<SchoolSizeBand | null>(null);
  readonly #requestedKind = signal<OrderKind>('license');

  /** Formulář, který se má po otevření chatu ukázat. */
  readonly requestedKind = this.#requestedKind.asReadonly();

  /** Tarif, na který se kliklo v ceníku. `null` = chat si nechá ten svůj. */
  readonly requestedTier = this.#requestedTier.asReadonly();

  /** Velikost školy vybraná v ceníku. `null` = chat si nechá tu svou. */
  readonly requestedSize = this.#requestedSize.asReadonly();

  /** Roste s každou žádostí zvenčí otevřít chat rovnou v objednávce. */
  readonly orderRequests = this.#orderRequests.asReadonly();

  readonly #identificationResource = httpResource<SchoolProfileIdentification>(() => {
    if (!this.#enabled() || !this.authService.isAuthenticated()) return undefined;
    const schoolId = this.#schoolIdOverride() ?? this.licenseState.currentSchoolId();
    if (!schoolId) return undefined;
    return `${environment.apiUrl}/schools/${schoolId}/profile/identification`;
  });

  readonly identification = computed<SchoolProfileIdentification | undefined>(() =>
    this.#identificationResource.status() === 'error'
      ? undefined
      : this.#identificationResource.value(),
  );

  readonly isLoading = this.#identificationResource.isLoading;

  /** Fakturační údaje lze doplnit jen tehdy, když je v profilu školy vyplněn aspoň název a IČO. */
  readonly hasBillingDetails = computed(() => {
    const identification = this.identification();
    return !!identification?.name?.trim() && !!identification?.ico?.trim();
  });

  /** Řádky fakturačních údajů tak, jak půjdou do zprávy. Prázdné pole se vynechává. */
  readonly billingLines = computed<string[]>(() => {
    const identification = this.identification();
    if (!identification) return [];
    return [
      ['Škola', identification.name],
      ['Adresa', identification.address],
      ['IČO', identification.ico],
      ['RED IZO', identification.redIzo],
    ]
      .filter(([, value]) => !!value?.trim())
      .map(([label, value]) => `${label}: ${value!.trim()}`);
  });

  /**
   * Nabídka měsíců se skládá znovu při každém otevření objednávky. `computed` bez závislosti na
   * čase by se spočítalo jednou při startu aplikace a v dlouho otevřené záložce by pak nabízelo
   * měsíc, který už začal.
   */
  readonly monthOptions = computed<MonthOption[]>(() => monthOptions(new Date(this.#openedAt())));

  constructor() {
    // Objednávka se dá odeslat i bez fakturačních údajů — chybu jen nahlásíme, uživatele s ní
    // netrápíme.
    effect(() => {
      if (this.#identificationResource.status() === 'error') {
        this.errorReporting.report(
          this.#identificationResource.error(),
          'LicenseOrderService.loadIdentification',
        );
      }
    });
  }

  enable(schoolId: string | null = null): void {
    this.#schoolIdOverride.set(schoolId);
    this.#openedAt.set(Date.now());
    this.#enabled.set(true);
  }

  /**
   * Otevře chat rovnou v objednávce licence — pro tlačítka mimo chat. Vlastní otevření obstará
   * {@link FloatingChat}, který je jediný, kdo o panelu ví.
   */
  requestOrder(
    schoolId: string | null = null,
    tier: LicenseType | null = null,
    size: SchoolSizeBand | null = null,
  ): void {
    this.enable(schoolId);
    this.#requestedKind.set('license');
    this.#requestedTier.set(tier);
    this.#requestedSize.set(size);
    this.#orderRequests.update((count) => count + 1);
  }

  /** Totéž pro přikoupení AI kreditů — tarif ani velikost školy se tu nevybírají. */
  requestCreditOrder(schoolId: string | null = null): void {
    this.enable(schoolId);
    this.#requestedKind.set('credits');
    this.#requestedTier.set(null);
    this.#requestedSize.set(null);
    this.#orderRequests.update((count) => count + 1);
  }
}

export function monthOptions(from = new Date()): MonthOption[] {
  const formatter = new Intl.DateTimeFormat('cs-CZ', { month: 'long', year: 'numeric' });
  // Po uzávěrce se aktuální měsíc z nabídky vypustí — nabízet ho by slibovalo termín, který už
  // nejde stihnout.
  const firstOffset = from.getDate() <= ORDER_DEADLINE_DAY ? 0 : 1;
  return Array.from({ length: MONTH_OPTION_COUNT }, (_, offset) => {
    const date = new Date(from.getFullYear(), from.getMonth() + firstOffset + offset, 1);
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return {
      value: `${date.getFullYear()}-${month}-01`,
      label: formatter.format(date),
    };
  });
}
